import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AnyFieldApi } from '@tanstack/react-form';
import { cn } from '@/shared/utils/utils';
import { useEmployees, useTeams } from '@/shared/hooks';
import { FieldInfo } from '@/shared/components/form/FieldInfo';
import {
  filterValidAssignees,
  getFullName,
} from '@/shared/utils/employee-utils';
import {
  MultiSelect,
  MultiSelectContent,
  MultiSelectTrigger,
  MultiSelectValue,
  MultiSelectGroup,
} from '@/shared/components/ui/MultiSelect';
import { UsersAndTeams } from '@momentum/types';
import { ViewerEmployeeItem } from './ViewerEmployeeItem';
import { ViewerTeamItem } from './ViewerTeamItem';

interface ViewerSelectorProps {
  field: AnyFieldApi;
  disabled?: boolean;
}

export const ViewerSelector: React.FC<ViewerSelectorProps> = ({
  field,
  disabled,
}) => {
  const { t } = useTranslation('goals');

  const { data: employees = [] } = useEmployees({
    isActive: true,
    isDeleted: false,
  });

  const { data: teams = [] } = useTeams({
    isDeleted: false,
  });

  const viewersValue = field.state.value as UsersAndTeams | undefined;

  const TEAM_PREFIX = 'team:';

  const selectedUserIds = useMemo(
    () => viewersValue?.users || [],
    [viewersValue?.users]
  );

  const selectedTeamIds = useMemo(
    () => viewersValue?.teams || [],
    [viewersValue?.teams]
  );

  const activeEmployees = useMemo(() => {
    const validEmployees = filterValidAssignees(
      employees.filter((emp) => emp.isActive)
    );
    return validEmployees.map((emp) => ({
      id: emp._id,
      fullName: getFullName(emp) || '',
      imageUrl: emp.personalInfo.imageUrl,
    }));
  }, [employees]);

  const activeTeams = useMemo(
    () =>
      teams.map((team) => ({
        id: `${TEAM_PREFIX}${team._id}`,
        name: team.name,
      })),
    [teams]
  );

  const selectedValues = useMemo(
    () => [
      ...selectedUserIds,
      ...selectedTeamIds.map((teamId) => `${TEAM_PREFIX}${teamId}`),
    ],
    [selectedUserIds, selectedTeamIds]
  );

  const handleValuesChange = (values: string[]) => {
    // Split values into users and teams
    const users: string[] = [];
    const teams: string[] = [];

    values.forEach((value) => {
      if (value.startsWith(TEAM_PREFIX)) {
        teams.push(value.replace(TEAM_PREFIX, ''));
      } else {
        users.push(value);
      }
    });

    field.handleChange({ users, teams });
    field.handleBlur();
  };

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-gray-900">
        {t('viewers')}
      </label>
      <MultiSelect values={selectedValues} onValuesChange={handleValuesChange}>
        <MultiSelectTrigger
          disabled={disabled}
          className={cn(
            'w-full min-h-[56px] bg-white border border-gray-300 rounded-lg hover:bg-white transition-colors',
            field.state.meta.errors.length > 0 && 'border-red-500'
          )}
        >
          <MultiSelectValue
            placeholder={t('selectViewers')}
            overflowBehavior="wrap-when-open"
          />
        </MultiSelectTrigger>
        <MultiSelectContent
          search={{
            placeholder: t('searchEmployees'),
            emptyMessage: t('noEmployeesFound'),
          }}
        >
          <MultiSelectGroup>
            {activeEmployees.map((employee) => (
              <ViewerEmployeeItem
                key={employee.id}
                id={employee.id}
                fullName={employee.fullName}
                imageUrl={employee.imageUrl}
              />
            ))}
            {activeTeams.map((team) => (
              <ViewerTeamItem key={team.id} id={team.id} name={team.name} />
            ))}
          </MultiSelectGroup>
        </MultiSelectContent>
      </MultiSelect>

      <FieldInfo field={field} />
    </div>
  );
};
